import axios from 'axios';
import {config} from './config';
import {isEmpty} from 'lodash';
import {TRACKING_IDENTITY_KEY, rememberTrackingIdentity} from './tracking';

export function refreshPage() {
    window.location.reload(false);
}

export function getUserFullDomain(username = null, path = '/admin') {
    let storakPath = config.storakPlusURL;

    if(username === null) {
        const user = readSession();
        username = user?.user?.username;
        if(username?.length <= 0) {
            return window.location.href;
        }
    }

    if(isEmpty(path)) {
        path = '/admin';
    }

    storakPath += path;

    return `https://${username}${storakPath}`;
}

export function navigateToUserStore(url = null, path = null) {
    if(url === null) {
        url = getUserFullDomain(null, path);
    }
    return window.location.href = url;
}

/** Everything the portal keeps about who is signed in. */
const SESSION_KEYS = ["userData", TRACKING_IDENTITY_KEY];

/**
 * Prefixes of the per-user entries written by the channel cards. The owner is
 * part of the key (see sessionScopedKey), so they are swept by prefix rather
 * than listed one by one — a new channel needs no change here.
 */
const SESSION_KEY_PREFIXES = [
    "fbUser:",
    "fbAccountConnected:",
    "igUser:",
    "igAccountConnected:",
    "waUser:",
    "waAccountConnected:",
];

/**
 * Keys earlier builds wrote unscoped. They outlived a logout and were then read
 * by whoever signed in next, so the portal showed one account another account's
 * connected Facebook profile. Nothing writes them any more; they are removed so
 * a browser that still carries them is cleaned on the next sign-in or sign-out.
 */
const LEGACY_SESSION_KEYS = [
    "fbUser",
    "fbAccountConnected",
    "igUser",
    "igAccountConnected",
    "waUser",
    "waAccountConnected",
    "token",
];

/** The stored session, or null when there is none or it is unreadable. */
export function readSession() {
    try {
        return JSON.parse(localStorage.getItem("userData") || "null");
    } catch (error) {
        return null;
    }
}

/** The signed-in portal user's id, or null when nobody is signed in. */
export function currentUserId() {
    return readSession()?.user?.id ?? null;
}

/**
 * A storage key that belongs to the signed-in user.
 *
 * Returns null when there is no session, which callers read as "nothing
 * remembered" — better than falling back to a shared key, which is how the
 * previous account's state leaked into the next one.
 */
export function sessionScopedKey(name) {
    const id = currentUserId();

    return id === null || id === undefined ? null : `${name}:${id}`;
}

export function clearSession() {
    [...SESSION_KEYS, ...LEGACY_SESSION_KEYS].forEach((key) =>
        localStorage.removeItem(key)
    );

    Object.keys(localStorage)
        .filter((key) => SESSION_KEY_PREFIXES.some((prefix) => key.startsWith(prefix)))
        .forEach((key) => localStorage.removeItem(key));
}

/**
 * The only way a session is written.
 *
 * It clears first: signing in as a second account while the first account's
 * entries are still in localStorage is what mixed the two. Overwriting
 * "userData" alone left everything else behind.
 */
export function startSession(data) {
    clearSession();
    localStorage.setItem("userData", JSON.stringify(data));
    rememberTrackingIdentity(data);
}

/**
 * A stored token that has expired is not a session. Without this an old
 * userData entry keeps redirecting the login page to the tenant subdomain the
 * user has already logged out of, with no way back to the portal login.
 */
export function hasLiveSession() {
    return isTokenLive(readSession()?.accessToken);
}

/** True when the JWT is readable and has not expired. */
export function isTokenLive(token) {
    if (!token) {
        return false;
    }

    try {
        const payload = JSON.parse(
            atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
        );

        return !payload.exp || payload.exp * 1000 > Date.now();
    } catch (error) {
        // Not a readable JWT: treat it as no session rather than trusting it.
        return false;
    }
}

/**
 * Sends the user into their own store's admin already signed in.
 *
 * storak-auth mints a one-time token — signed with the secret both apps share,
 * good for two minutes, burned in the shared Redis on first use — and the store
 * redeems it at /admin/console-login. Without this the user reaches the store
 * only to be shown its login form and type the same password a second time.
 *
 * Anything that goes wrong falls back to the plain redirect, which still lands
 * on the store, just at its login page.
 */
export async function navigateToUserStoreSignedIn(path = null) {
    const userData = readSession();

    if (userData?.accessToken) {
        try {
            const {data} = await axios.get(
                `${config.storakAuthApiUrl}/api/auth/store-login-link`,
                {headers: {Authorization: `Bearer ${userData.accessToken}`}}
            );

            if (data?.url) {
                window.location.href = data.url;

                return;
            }
        } catch (error) {
            // Falls through to the ordinary redirect below.
        }
    }

    return navigateToUserStore(null, path);
}

/** Renew once the token is more than a day old, so one call covers a session. */
const REFRESH_AFTER_SECONDS = 86400;

/**
 * Slides the portal session forward while the user is active.
 *
 * The store admin session and this one are renewed on different schedules, so
 * without this the portal could consider the user signed out while their store
 * admin was still open and working. Failure is deliberately silent: the token
 * already in hand keeps working until it genuinely expires.
 */
export async function refreshSessionIfNeeded() {
    if (!hasLiveSession()) {
        return;
    }

    const userData = readSession();

    try {
        const payload = JSON.parse(
            atob(userData.accessToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
        );

        const age = Math.floor(Date.now() / 1000) - (payload.iat ?? 0);

        if (age < REFRESH_AFTER_SECONDS) {
            return;
        }
    } catch (error) {
        return;
    }

    try {
        const {data} = await axios.get(
            `${config.storakAuthApiUrl}/api/auth/refresh`,
            {headers: {Authorization: `Bearer ${userData.accessToken}`}}
        );

        if (!data?.accessToken) {
            return;
        }

        // The request was in flight for a while: the user may have logged out,
        // or logged in as somebody else, in the meantime. Writing the merged
        // object blindly would resurrect the session that was just ended, or
        // overwrite the new one with the old user's token.
        const current = readSession();

        if (!current || current.accessToken !== userData.accessToken) {
            return;
        }

        localStorage.setItem(
            "userData",
            JSON.stringify({...current, ...data})
        );
    } catch (error) {
        // Keep the current token; it is still valid.
    }
}

/**
 * The portal page to return to after signing in (`?next=`), e.g. the
 * Messenger connect page the store admin sent the user to. Only same-site
 * paths; anything else is ignored so it cannot become an open redirect.
 */
export function safeNext() {
    const next = new URLSearchParams(window.location.search).get("next") || "";

    return /^\/(?!\/)[A-Za-z0-9/_?=&.%-]{0,200}$/.test(next) ? next : null;
}
